
import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import CategoryDetail from '../components/CategoryDetail';
import Settings from '../components/Settings';
import Orders from './Orders';
import CreateCategoryModal from '../components/CreateCategoryModal';
import OrderHistory from './OrderHistory';
import Analytics from '../components/Analytics';
import Offers from './Offers';

export default function Dashboard() {
    const { logout, dbUser } = useAuth();
    const navigate = useNavigate();

    // State
    const [activeView, setActiveView] = useState('overview'); // 'overview', 'settings', 'category-ID', 'uncategorized'
    const [categories, setCategories] = useState([]);
    const [uncategorizedItems, setUncategorizedItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);

    // Theme State
    const [isDarkMode, setIsDarkMode] = useState(() => {
        return localStorage.getItem('theme') === 'dark';
    });

    useEffect(() => {
        localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
        document.body.style.backgroundColor = isDarkMode ? '#121212' : '#f5f5f5';
    }, [isDarkMode]);

    useEffect(() => {
        fetchMenu();
    }, []);

    const fetchMenu = async () => {
        try {
            const res = await api.get('/restaurant/menu');
            setCategories(res.data.categories);
            setUncategorizedItems(res.data.uncategorized || []);
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    const handleOpenCreateCategory = () => {
        setShowCreateCategoryModal(true);
    };

    const handleCreateCategory = async (categoryData) => {
        try {
            await api.post('/restaurant/category', categoryData);
            await fetchMenu();
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const handleUpdateCategory = async (id, categoryData) => {
        try {
            await api.put(`/restaurant/category/${id}`, categoryData);
            await fetchMenu();
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const handleDeleteCategory = async (id) => {
        try {
            await api.delete(`/restaurant/category/${id}`);
            // If active view was this category, switch to overview
            if (activeView === `category-${id}`) {
                setActiveView('overview');
            }
            await fetchMenu();
        } catch (error) {
            console.error(error);
            alert("Failed to delete category: " + (error.response?.data?.error || error.message));
        }
    };

    const handleReorderCategories = async (newOrder) => {
        // Optimistic update locally could be done here if we passed setCategories down, 
        // but for now we'll just let the API call finish and fetchMenu update it or 
        // sidebar can handle local state while dragging.
        try {
            const payload = newOrder.map((cat, index) => ({
                id: cat.id,
                sortOrder: index
            }));
            await api.put('/restaurant/category/reorder', payload);
            // Silent fetch to sync
            fetchMenu();
        } catch (error) {
            console.error("Reorder failed", error);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const getActiveCategory = () => {
        if (activeView === 'uncategorized') {
            return {
                id: 'uncategorized',
                name: 'Uncategorized Items',
                items: uncategorizedItems,
                isVirtual: true // Flag to hide "Delete Category" button
            };
        }
        if (activeView.startsWith('category-')) {
            const id = activeView.split('category-')[1];
            return categories.find(c => c.id === id);
        }
        return null;
    };

    const activeCategory = getActiveCategory();
    const styles = getStyles(isDarkMode);

    return (
        <div style={styles.container}>
            <Sidebar
                categories={categories}
                uncategorizedItems={uncategorizedItems}
                activeView={activeView}
                onSelectView={setActiveView}
                onCreateCategory={handleOpenCreateCategory}
                onUpdateCategory={handleUpdateCategory}
                onDeleteCategory={handleDeleteCategory}
                onReorderCategories={handleReorderCategories}
                isDarkMode={isDarkMode}
                onLogout={handleLogout}
                restaurant={dbUser?.restaurant}
            />

            <div style={styles.mainContent}>
                {loading ? (
                    <div style={{ padding: '2rem' }}>Loading...</div>
                ) : (
                    <>
                        {activeView === 'overview' && (
                            <Analytics isDarkMode={isDarkMode} />
                        )}

                        {activeView === 'orders' && (
                            <Orders isDarkMode={isDarkMode} />
                        )}

                        {activeView === 'history' && (
                            <OrderHistory isDarkMode={isDarkMode} />
                        )}


                        {activeView === 'settings' && (
                            <Settings isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
                        )}

                        {activeView === 'offers' && (
                            <Offers isDarkMode={isDarkMode} />
                        )}

                        {activeCategory && (
                            <CategoryDetail
                                category={activeCategory}
                                categories={categories} // Pass all categories for assignment
                                onRefresh={fetchMenu}
                                isDarkMode={isDarkMode}
                            />
                        )}
                    </>
                )}
            </div>

            <CreateCategoryModal
                isOpen={showCreateCategoryModal}
                onClose={() => setShowCreateCategoryModal(false)}
                onCreate={handleCreateCategory}
                categories={categories}
                isDarkMode={isDarkMode}
            />
        </div>
    );
}

const getStyles = (isDark) => ({
    container: {
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: isDark ? '#121212' : '#f5f5f5',
        color: isDark ? '#fff' : '#333'
    },
    mainContent: {
        marginLeft: '250px',
        flex: 1,
        padding: '0',
        height: '100vh',
        overflowY: 'auto'
    },
    overview: {
        padding: '30px',
        color: isDark ? '#fff' : '#333'
    },
    statsGrid: {
        display: 'flex',
        gap: '20px',
        marginTop: '20px'
    },
    statCard: {
        padding: '20px',
        backgroundColor: isDark ? '#2a2a2a' : '#fff',
        borderRadius: '8px',
        boxShadow: isDark ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
        border: isDark ? '1px solid #333' : '1px solid #ddd',
        minWidth: '150px'
    },
    statValue: {
        fontSize: '2rem',
        fontWeight: 'bold',
        color: '#0F9D58',
        margin: '10px 0 0 0'
    }
});
